import {
    Box,
    ListItemIcon,
    alpha,
    useTheme
} from '@mui/material';
import {
    EagerIssue,
    IssuePriority,
    PermissionType
} from '../../models';
import {
    getUpdateIssueCallback,
    issueByIssueIdentifierSelectorFamily
} from '../../lib/design/document/state/DocumentState';
import {
    useMemo,
    useRef,
    useState
} from 'react';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ContextAwareIdentifier } from '../../lib/design/document/ContextAwareIdentifier';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import Grow from '@mui/material/Grow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { ModelType } from '../../lib/design/document/ModelType';
import Paper from '@mui/material/Paper';
import { PermissionResourceType } from '../../models';
import { PermissionRestrictedButton } from '../general/button/PermissionRestrictedButton';
import Popper from '@mui/material/Popper';
import { StateContext } from '../../lib/design/document/state/StateContext';
import { issuePriorityText } from '../../componentsV2/issue/issuesPriorityText';
import { useRecoilValue } from 'recoil';

type PriorityDetails = {
    icon: JSX.Element;
    text: string;
    color: any;
};

const PRIORITY_TO_DETAIL_MAP = new Map<IssuePriority, PriorityDetails>(
    [
        [
            IssuePriority.HIGH,
            {
                icon: <KeyboardArrowUpIcon />,
                text: issuePriorityText[IssuePriority.HIGH],
                color: "#FF0000"
            }
        ],
        [
            IssuePriority.MEDIUM,
            {
                icon: <DragHandleIcon />,
                text: issuePriorityText[IssuePriority.MEDIUM],
                color: "#cb9d06"
            }
        ],
        [
            IssuePriority.LOW,
            {
                icon: <KeyboardArrowDownIcon />,
                text: issuePriorityText[IssuePriority.LOW],
                color: "#2ecc40"
            }
        ]
    ]
);

interface IssuePrioritySelectMenuProps {
    readonly issueId: string;
    readonly size: "small" | "medium" | "large";
    readonly stateContext: StateContext;
}

export const IssuePrioritySelectMenu = ({ issueId, size, stateContext }: IssuePrioritySelectMenuProps) => {
    /* Internal Page State */
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const issueIdentifier = useMemo<ContextAwareIdentifier>(() => {
        return new ContextAwareIdentifier(issueId, stateContext, ModelType.ISSUE);
    }, [issueId, stateContext]);
    const issue = useRecoilValue<EagerIssue | null>(issueByIssueIdentifierSelectorFamily(issueIdentifier));

    /* Dependencies */
    const theme = useTheme();
    const updateIssuePriority = getUpdateIssueCallback();

    const buttonDetails: PriorityDetails = useMemo(() => {
        return PRIORITY_TO_DETAIL_MAP.get((IssuePriority[(issue?.priority ?? IssuePriority.MEDIUM)])!)!;
    }, [issue?.priority]);

    const handleMenuItemClick = (priority: IssuePriority) => {
        updateIssuePriority({
            ...issue!,
            priority: priority
        }, stateContext);
        setIsOpen(false);
    };

    return (
        <>
            <Box
                ref={anchorRef}
            >
                <PermissionRestrictedButton
                    resourceType={PermissionResourceType.ISSUE}
                    size={size}
                    onClick={() => setIsOpen(true)}
                    startIcon={buttonDetails.icon}
                    sx={{
                        color: buttonDetails.color,
                        borderColor: buttonDetails.color,
                        backgroundColor: "transparent",
                        "&:hover": {
                            color: alpha(buttonDetails.color, 0.9),
                            borderColor: buttonDetails.color,
                        }
                    }}
                    resourceId={issueId}
                    requiredPermission={PermissionType.EDIT} //Issue status is part of the issue record, so it must be restricted by EDIT permission and not MANAGE_STATE
                >
                    {buttonDetails.text}
                </PermissionRestrictedButton>
            </Box>
            <Popper
                style={{
                    zIndex: 2 // Force it to be displayed "on top" of the text field labels.
                }}
                open={isOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom" ? "center top" : "center bottom"
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {Array.from(PRIORITY_TO_DETAIL_MAP.entries()).map(([priority, details]) => (
                                        <MenuItem
                                            key={priority}
                                            selected={priority === issue?.priority}
                                            onClick={() => handleMenuItemClick(priority)}
                                        >
                                            <ListItemIcon>
                                                {details.icon}
                                            </ListItemIcon>
                                            {details.text}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

IssuePrioritySelectMenu.defaultProps = {
    size: "medium"
};